import * as React from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import { HiMailOpen, HiPhoneOutgoing } from 'react-icons/hi';
import {
  Flex,
  Text,
  Box,
  Icon,
  Textarea,
  Button,
  Image,
  Stack,
  Select,
  VisuallyHidden,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Container, SimpleInput } from 'components';
import * as z from 'zod';
import Banner from '../../assets/images/banner.png';
import { selectOptions } from './consts';

const FaqSchema = z.object({
  name: z.string().nonempty({ message: 'Campo obrigatório' }),
  email: z
    .string()
    .nonempty('Campo obrigatório')
    .email('Email inválido')
    .optional(),
  phone: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' })
    .refine(value => !value.includes('_'), 'Celular inválido'),
  select: z.string().nonempty('Campo obrigatório'),
  mensage: z.string(),
});

type FaqSchemaProps = z.infer<typeof FaqSchema>;

export const CallCenter: React.FC = () => {
  const {
    register,
    formState: { errors },
  } = useForm<FaqSchemaProps>({
    resolver: zodResolver(FaqSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  return (
    <Container bg="white">
      <Box
        display={{ base: 'none', md: 'flex' }}
        mx="16px"
        justifyContent="center"
      >
        <Image alt="" src={Banner} />
      </Box>

      <Box
        p="32px"
        mt={{ base: '0px', md: '48px' }}
        border="1px solid"
        borderColor={{ base: 'transparent', xl: 'grey.100' }}
        mr="auto"
        ml="auto"
        w={{ base: '100%', xl: '62%' }}
        borderRadius="8px"
      >
        <Text
          fontWeight={700}
          fontSize="24px"
          textAlign={{ base: 'start', md: 'center' }}
          color="ocean.200"
        >
          Canais de atendimento
        </Text>

        <Text
          textAlign={{ base: 'justify', md: 'center' }}
          mx="auto"
          w={{ base: '100%', md: '60%' }}
          fontSize="12px"
          color="text.200"
        >
          Por favor, preencha os campos abaixo que nossa equipe entrará em
          contato com você. Horário de atendimento: segunda a sexta das 08h às
          18hrs.
        </Text>
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          justifyContent="space-around"
          alignItems={{ base: 'center', md: 'start' }}
          mt="40px"
        >
          <Box w={{ base: '100%', md: '50%' }}>
            <Stack gap="24px">
              <SimpleInput
                placeholder="Digite seu nome completo"
                labelColor="text.100"
                borderColor="grey.500"
                label="*Nome completo"
                color="text.100"
                {...register('name')}
                error={errors?.name?.message}
              />
              <SimpleInput
                placeholder="Digite seu e-mail"
                labelColor="text.100"
                borderColor="grey.500"
                label="*E-mail"
                type="email"
                color="text.100"
                {...register('email')}
                error={errors?.email?.message}
              />
              <SimpleInput
                placeholder="(99)9999-9999"
                labelColor="text.100"
                borderColor="grey.500"
                label="*Celular"
                color="text.100"
                type="phone"
                {...register('phone')}
                error={errors?.phone?.message}
              />

              <Stack>
                <Text
                  aria-hidden="true"
                  as="label"
                  fontSize="14px"
                  color="text.100"
                >
                  *Informe o motivo do seu contato
                </Text>
                <Select
                  {...register('select')}
                  borderColor="grey.500"
                  placeholder="Selecionar"
                >
                  {selectOptions.map(item => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>

                {errors.select?.message && (
                  <>
                    <VisuallyHidden>
                      <Text>Erro, {errors.select?.message}</Text>
                    </VisuallyHidden>
                    <Text
                      aria-hidden="true"
                      alignSelf="start"
                      pl="8px"
                      color="red.200"
                      fontSize="sm"
                    >
                      {errors.select?.message}
                    </Text>
                  </>
                )}
              </Stack>

              <Stack>
                <Text
                  aria-hidden="true"
                  as="label"
                  fontSize="14px"
                  color="text.100"
                >
                  Mensagem
                </Text>
                <Textarea
                  {...register('mensage')}
                  aria-label="Adicione detalhes da solicitação"
                  variant="unstyled"
                  bg="transparent"
                  border="1px solid"
                  borderColor="grey.500"
                  color="text.100"
                  fontSize="16px"
                  h="156px"
                  p="3"
                  size="lg"
                />
              </Stack>
              <Button w={{ base: '100%', md: '260px' }}>
                Entrar em contato com atendimento
              </Button>
            </Stack>
          </Box>

          <Flex
            mt={{ base: '16px', md: '0px' }}
            w="256px"
            flexDir="column"
            ml={{ base: '0px', md: '16px' }}
            borderRadius="8px"
            h="100%"
            alignItems={{ base: 'start', md: 'center' }}
            bg="grey.100"
            py="32px"
          >
            <Text
              ml={{ base: '12px', md: '0px' }}
              fontSize="14px"
              color="text.200"
            >
              Outras formas de nos contactar
            </Text>
            <Flex flexDirection={{ base: 'column' }} justifyContent="center">
              <Flex mt="8px" ml="8px" alignItems="center">
                <Icon mr="8px" color="red" as={HiPhoneOutgoing} />
                <Box>
                  <Text fontWeight={600} color="button" fontSize="12px">
                    Telefone
                  </Text>
                  <Text
                    textDecoration="underline"
                    fontSize="12px"
                    color="text.100"
                  >
                    0800 025 4141
                  </Text>
                </Box>
              </Flex>

              <Flex mt="8px" ml="8px" alignItems="center">
                <Icon mr="8px" color="red" as={HiMailOpen} />
                <Box>
                  <Text fontWeight={600} color="button" fontSize="12px">
                    E-mail
                  </Text>
                  <Text
                    textDecoration="underline"
                    fontSize="12px"
                    color="text.100"
                  >
                    sac@grupovamos.com.br
                  </Text>
                </Box>
              </Flex>
              <Flex mt="8px" ml="8px" alignItems="center">
                <Icon mr="8px" color="red" as={AiOutlineWhatsApp} />
                <Box>
                  <Text fontWeight={600} color="button" fontSize="12px">
                    WhatsApp
                  </Text>
                  <Text
                    textDecoration="underline"
                    fontSize="12px"
                    color="text.100"
                  >
                    (21) 98036-0397
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Container>
  );
};
